'use client'

/**
 * 開きっぱなしの古いタブが Server Action を呼んで 404 になったとき、理由と直し方を出す。
 *
 * デプロイ前に開いた画面の Server Action ID は、新しいサーバーに存在しない。Next はこのとき
 * 応答に `x-nextjs-action-not-found: 1` を付けて 404 を返し、呼び出し側へ例外を投げる
 * （本番 2026-09-14: POST /bt-progress 9件・POST /leads 1件）。呼び出し側の catch は汎用の
 * 「失敗しました」しか出せず、再読み込みするまで何度押しても直らない。
 *
 * Server Action の fetch は Next 内部から都度 global fetch を呼ぶため、ここで包んで応答を見る。
 * 応答は加工せずそのまま返す（Next の例外処理・呼び出し側の catch は従来どおり動く）。
 * 自動で再読み込みはしない＝入力途中の内容を消さない（私の判断。根拠は AUTH-1 #15666 の
 * コミット文「deploymentId の自動ハードリロードは入力途中データに影響するため見送り（HQ協議）」）。
 *
 * 回帰ガード: lib/providers/__tests__/stale-deployment-notice.test.tsx
 */

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { toast } from 'sonner'
import { logError } from '@/lib/errors'

export const STALE_DEPLOYMENT_TOAST_ID = 'stale-deployment'

export function isUnrecognizedServerActionResponse(res: Response): boolean {
  return res.status === 404 && res.headers.get('x-nextjs-action-not-found') === '1'
}

/** 登録ごとに1要素（同じ関数を2回登録しても、片方を外した時にもう片方を消さない）。 */
const listeners: { fn: () => void }[] = []
let baseFetch: typeof fetch | null = null
let wrappedFetch: typeof fetch | null = null
/** これまでに作った包み（包み直しで世代が増えても「自分の包み」と分かるように）。 */
const ownWrappers = new WeakSet<typeof fetch>()
/** 包みが二重に重なっても、1つの応答で通知するのは1回だけ。 */
const notifiedResponses = new WeakSet<Response>()

function wrapCurrentFetch() {
  const original = globalThis.fetch
  baseFetch = original
  wrappedFetch = async (...args: Parameters<typeof fetch>) => {
    const res = await original(...args)
    if (isUnrecognizedServerActionResponse(res) && !notifiedResponses.has(res)) {
      notifiedResponses.add(res)
      // 同じ関数が二重に登録されていても 1 回だけ呼ぶ。通知の失敗で応答を失敗扱いにしない
      new Set(listeners.map((l) => l.fn)).forEach((fn) => {
        try {
          fn()
        } catch (err) {
          logError(err, 'stale-deployment-notice: 通知に失敗しました')
        }
      })
    }
    return res
  }
  ownWrappers.add(wrappedFetch)
  globalThis.fetch = wrappedFetch
}

/** global fetch を1回だけ包む。戻り値で外す（全員が外したら元の fetch に戻す）。 */
export function installStaleActionFetchWatcher(onDetect: () => void): () => void {
  const entry = { fn: onDetect }
  listeners.push(entry)
  if (listeners.length === 1) wrapCurrentFetch()

  let removed = false
  return () => {
    if (removed) return
    removed = true
    listeners.splice(listeners.indexOf(entry), 1)
    if (listeners.length === 0 && baseFetch) {
      // 後から別の仕組みが fetch を包み直していたら、それを外さない（自分の包みの時だけ戻す）
      if (globalThis.fetch === wrappedFetch) globalThis.fetch = baseFetch
      baseFetch = null
      wrappedFetch = null
    }
  }
}

/**
 * 見張りが別の仕組みに外されていたら、今の fetch の上に包み直す。
 * 下見画面の PreviewWriteGuard 等は「先に包み、外す時に無条件で元へ戻す」ため、その内側に乗った
 * この見張りごと剥がれる（2026-09-15 門番指摘）。相手の戻し方は書き込み止めの安全装置なので変えず、こちらが戻る。
 */
export function ensureStaleActionFetchWatcher(): void {
  // 今の fetch が自分の包み（どの世代でも）なら見張りは生きている。戻された時だけ包み直す
  if (listeners.length === 0 || ownWrappers.has(globalThis.fetch)) return
  wrapCurrentFetch()
}

function showStaleDeploymentToast() {
  toast.warning(
    'G-force が新しい版に更新されたため、この画面の操作を受け付けられませんでした。入力中の内容があれば控えてから、再読み込みしてください。',
    {
      id: STALE_DEPLOYMENT_TOAST_ID,
      duration: Infinity,
      action: { label: '再読み込み', onClick: () => window.location.reload() },
    }
  )
}

/** 画面には何も描かない（ルートレイアウトに1つ置き、案内は sonner の Toaster に出す）。 */
export function StaleDeploymentNotice(): null {
  const pathname = usePathname()
  useEffect(() => installStaleActionFetchWatcher(showStaleDeploymentToast), [])
  // 画面を移るたびに、他の画面の仕組みに外された見張りを戻す（子の後片付けは親の effect より先に走る）
  useEffect(() => ensureStaleActionFetchWatcher(), [pathname])
  return null
}
